/* ==========================================================================
   Anayansi Artworks Theme - Global JavaScript
   ========================================================================== */

(function() {
  'use strict';

  /* --------------------------------------------------------------------------
     Header & Navigation
     -------------------------------------------------------------------------- */

  const Header = {
    init() {
      this.header = document.querySelector('[data-header]');
      this.menuToggle = document.querySelector('[data-menu-toggle]');
      this.mobileNav = document.querySelector('[data-mobile-nav]');
      this.dropdownToggles = document.querySelectorAll('.mobile-nav__link--dropdown');

      if (this.menuToggle && this.mobileNav) {
        this.bindEvents();
      }
    },

    bindEvents() {
      // Mobile menu toggle
      this.menuToggle.addEventListener('click', () => this.toggleMobileMenu());

      // Mobile dropdown toggles
      this.dropdownToggles.forEach(toggle => {
        toggle.addEventListener('click', (e) => this.toggleDropdown(e));
      });

      // Close mobile menu on escape
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isMenuOpen()) {
          this.closeMobileMenu();
        }
      });

      // Close mobile menu when clicking outside
      document.addEventListener('click', (e) => {
        if (this.isMenuOpen() && !this.mobileNav.contains(e.target) && !this.menuToggle.contains(e.target)) {
          this.closeMobileMenu();
        }
      });
    },

    toggleMobileMenu() {
      const isOpen = this.isMenuOpen();
      this.menuToggle.setAttribute('aria-expanded', !isOpen);
      this.mobileNav.setAttribute('aria-hidden', isOpen);
      document.body.style.overflow = isOpen ? '' : 'hidden';
    },

    closeMobileMenu() {
      this.menuToggle.setAttribute('aria-expanded', 'false');
      this.mobileNav.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    },

    isMenuOpen() {
      return this.menuToggle.getAttribute('aria-expanded') === 'true';
    },

    toggleDropdown(e) {
      const toggle = e.currentTarget;
      const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', !isExpanded);
    }
  };

  /* --------------------------------------------------------------------------
     Product Form
     -------------------------------------------------------------------------- */

  const ProductForm = {
    init() {
      this.priceFormatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      });
      document.querySelectorAll('[data-product]').forEach(product => this.setupProduct(product));
    },

    setupProduct(product) {
      const form = product.querySelector('[data-product-form]');
      if (!form) return;

      const optionSelects = product.querySelectorAll('[data-option-select]');
      const quantityInput = product.querySelector('[data-quantity-input]');
      const quantityMinus = product.querySelector('[data-quantity-minus]');
      const quantityPlus = product.querySelector('[data-quantity-plus]');

      optionSelects.forEach(select => {
        select.addEventListener('change', () => this.updateVariant(product));
      });

      if (quantityMinus && quantityPlus && quantityInput) {
        quantityMinus.addEventListener('click', () => {
          const current = parseInt(quantityInput.value, 10) || 1;
          if (current > 1) quantityInput.value = current - 1;
          this.updateStepperState(product);
        });

        quantityPlus.addEventListener('click', () => {
          const current = parseInt(quantityInput.value, 10) || 1;
          const max = parseInt(quantityInput.max, 10);
          if (!isNaN(max) && current >= max) return;
          quantityInput.value = current + 1;
          this.updateStepperState(product);
        });

        quantityInput.addEventListener('input', () => this.updateStepperState(product));
        quantityInput.addEventListener('change', () => {
          this.clampQuantity(quantityInput);
          this.updateStepperState(product);
        });
      }

      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const matched = this.getCurrentVariant(product);
        if (!matched || !matched.available) return;

        const addBtn = form.querySelector('[name="add"]');
        const originalText = addBtn ? addBtn.textContent : 'Add to cart';
        if (addBtn) {
          addBtn.disabled = true;
          addBtn.textContent = 'Adding…';
        }
        this.clearError(product);

        const enforceable =
          matched.inventory_management === 'shopify' &&
          matched.inventory_policy === 'deny' &&
          typeof matched.inventory_quantity === 'number';

        const requested = quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1;

        if (enforceable) {
          let liveQty = 0;
          try {
            const response = await fetch('/cart.js', {
              headers: { Accept: 'application/json' },
              cache: 'no-store',
            });
            if (response.ok) {
              const cart = await response.json();
              liveQty = cart.items
                .filter((item) => item.variant_id === matched.id)
                .reduce((sum, item) => sum + item.quantity, 0);
            }
          } catch (err) {
            // proceed — server will enforce
          }

          const max = Math.max(0, matched.inventory_quantity - liveQty);
          if (requested > max) {
            this.showError(
              product,
              max === 0
                ? 'Already at the maximum available in your cart.'
                : `Only ${max} more available — you already have ${liveQty} in your cart.`
            );
            this.applyVariantState(product, matched, liveQty);
            return;
          }
        }

        // The quantity input lives in a different schema block (outside the form),
        // so a native form submit only carries `id` and defaults quantity to 1.
        // POST explicitly via /cart/add.js to send the requested quantity.
        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({ id: matched.id, quantity: requested }),
          });
          if (response.ok) {
            window.location.href = '/cart';
            return;
          }
          const err = await response.json().catch(() => ({}));
          this.showError(product, err.description || err.message || 'Could not add to cart.');
        } catch (err) {
          this.showError(product, 'Network error — please try again.');
        } finally {
          if (addBtn) {
            addBtn.disabled = false;
            addBtn.textContent = originalText;
          }
        }
      });

      // Apply limits for the initially-selected variant on page load
      this.updateVariant(product);
    },

    getVariants(product) {
      const script = product.querySelector('[data-product-variants]');
      if (!script) return [];
      try { return JSON.parse(script.textContent); } catch (e) { return []; }
    },

    getCartQuantities(product) {
      const script = product.querySelector('[data-cart-items]');
      const map = {};
      if (!script) return map;
      try {
        const items = JSON.parse(script.textContent);
        items.forEach(item => {
          map[item.variant_id] = (map[item.variant_id] || 0) + item.quantity;
        });
      } catch (e) { /* ignore */ }
      return map;
    },

    getCurrentVariant(product) {
      const variants = this.getVariants(product);
      const selects = product.querySelectorAll('[data-option-select]');
      if (selects.length === 0) return variants[0]; // single-variant product
      const selectedValues = Array.from(selects).map(s => s.value);
      return variants.find(v =>
        Array.isArray(v.options) &&
        v.options.length === selectedValues.length &&
        v.options.every((opt, i) => opt === selectedValues[i])
      );
    },

    /**
     * Returns max quantity that may still be added to cart for this variant.
     * `null` means no limit (inventory not tracked, or policy allows oversell).
     * `0` means already at the limit.
     */
    getMaxFor(variant, cartQty) {
      if (!variant) return 0;
      if (variant.inventory_management !== 'shopify') return null;
      if (variant.inventory_policy === 'continue') return null;
      // If inventory_quantity isn't exposed, can't enforce client-side — let server decide
      if (typeof variant.inventory_quantity !== 'number') return null;
      const inCart = cartQty[variant.id] || 0;
      return Math.max(0, variant.inventory_quantity - inCart);
    },

    clampQuantity(input) {
      const max = parseInt(input.max, 10);
      let val = parseInt(input.value, 10) || 1;
      if (val < 1) val = 1;
      if (!isNaN(max) && max > 0 && val > max) val = max;
      input.value = val;
    },

    updateVariant(product) {
      const matched = this.getCurrentVariant(product);
      if (!matched) return;

      const variantIdInput = product.querySelector('[data-variant-id]');
      if (variantIdInput) variantIdInput.value = matched.id;

      const priceEl = product.querySelector('[data-product-price]');
      if (priceEl && typeof matched.price === 'number') {
        priceEl.textContent = this.priceFormatter.format(matched.price / 100);
      }

      const cartQty = this.getCartQuantities(product);
      const inCart = cartQty[matched.id] || 0;
      this.applyVariantState(product, matched, inCart);
    },

    applyVariantState(product, variant, inCart) {
      const max = this.getMaxFor(variant, { [variant.id]: inCart });

      const quantityInput = product.querySelector('[data-quantity-input]');
      if (quantityInput) {
        if (max === null) {
          quantityInput.removeAttribute('max');
        } else {
          quantityInput.max = String(Math.max(1, max));
        }
        this.clampQuantity(quantityInput);
        if (max === 0) quantityInput.value = 1;
      }

      const addBtn = product.querySelector('[name="add"]');
      if (addBtn) {
        if (!variant.available) {
          addBtn.disabled = true;
          addBtn.textContent = 'Sold out';
        } else if (max === 0) {
          addBtn.disabled = true;
          addBtn.textContent = inCart > 0 ? 'Max already in cart' : 'Sold out';
        } else {
          addBtn.disabled = false;
          addBtn.textContent = 'Add to cart';
        }
      }

      this.updateStepperState(product);
    },

    updateStepperState(product) {
      const quantityInput = product.querySelector('[data-quantity-input]');
      if (!quantityInput) return;
      const current = parseInt(quantityInput.value, 10) || 1;
      const max = parseInt(quantityInput.max, 10);
      const plusBtn = product.querySelector('[data-quantity-plus]');
      const minusBtn = product.querySelector('[data-quantity-minus]');
      if (plusBtn) plusBtn.disabled = !isNaN(max) && current >= max;
      if (minusBtn) minusBtn.disabled = current <= 1;
    },

    showError(product, message) {
      const errEl = product.querySelector('[data-product-error]');
      if (!errEl) return;
      errEl.textContent = message;
      errEl.hidden = false;
    },

    clearError(product) {
      const errEl = product.querySelector('[data-product-error]');
      if (!errEl) return;
      errEl.textContent = '';
      errEl.hidden = true;
    },
  };

  /* --------------------------------------------------------------------------
     Product Media Gallery
     -------------------------------------------------------------------------- */

  const ProductGallery = {
    init() {
      this.galleries = document.querySelectorAll('[data-product-media-main]');
      this.galleries.forEach(gallery => this.setupGallery(gallery));
    },

    setupGallery(gallery) {
      const thumbnails = gallery.parentElement.querySelectorAll('[data-thumbnail]');

      thumbnails.forEach(thumbnail => {
        thumbnail.addEventListener('click', () => {
          const mediaId = thumbnail.dataset.mediaId;
          this.switchMedia(gallery, thumbnails, mediaId);
        });
      });
    },

    switchMedia(gallery, thumbnails, mediaId) {
      // Update active states
      const mediaItems = gallery.querySelectorAll('[data-media-id]');

      mediaItems.forEach(item => {
        item.classList.toggle('is-active', item.dataset.mediaId === mediaId);
      });

      thumbnails.forEach(thumb => {
        thumb.classList.toggle('is-active', thumb.dataset.mediaId === mediaId);
      });
    }
  };

  /* --------------------------------------------------------------------------
     Cart
     -------------------------------------------------------------------------- */

  const Cart = {
    init() {
      this.cartForm = document.querySelector('[data-cart-form]');
      if (!this.cartForm) return;

      this.timers = new WeakMap();
      this.formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      });
      this.bindEvents();
    },

    bindEvents() {
      this.cartForm.addEventListener('click', (e) => {
        const minus = e.target.closest('[data-quantity-minus]');
        const plus = e.target.closest('[data-quantity-plus]');
        const remove = e.target.closest('[data-remove-item]');

        if (minus) {
          e.preventDefault();
          this.handleStepper(minus, -1);
        } else if (plus) {
          e.preventDefault();
          this.handleStepper(plus, 1);
        } else if (remove) {
          e.preventDefault();
          this.handleRemove(remove);
        }
      });

      this.cartForm.addEventListener('input', (e) => {
        const input = e.target.closest('[data-quantity-input]');
        if (!input) return;
        const item = input.closest('[data-cart-item]');
        if (item) this.updateStepperState(item);
        this.scheduleUpdate(input, 600);
      });

      // Apply initial stepper state for every line
      this.cartForm.querySelectorAll('[data-cart-item]').forEach(item => {
        this.applyMaxAttr(item);
        this.updateStepperState(item);
      });
    },

    applyMaxAttr(item) {
      const max = this.getMaxForItem(item);
      const input = item.querySelector('[data-quantity-input]');
      if (!input) return;
      if (max === null) {
        input.removeAttribute('max');
      } else {
        input.max = String(Math.max(1, max));
      }
    },

    getMaxForItem(item) {
      const mgmt = item.dataset.inventoryManagement;
      const policy = item.dataset.inventoryPolicy;
      const qty = parseInt(item.dataset.inventoryQuantity, 10);
      if (mgmt !== 'shopify') return null;
      if (policy === 'continue') return null;
      if (isNaN(qty)) return null;
      return qty;
    },

    updateStepperState(item) {
      const input = item.querySelector('[data-quantity-input]');
      const plusBtn = item.querySelector('[data-quantity-plus]');
      if (!input || !plusBtn) return;
      const current = parseInt(input.value, 10) || 0;
      const max = this.getMaxForItem(item);
      plusBtn.disabled = max !== null && current >= max;
    },

    handleStepper(btn, delta) {
      const item = btn.closest('[data-cart-item]');
      if (!item) return;
      const input = item.querySelector('[data-quantity-input]');
      if (!input) return;
      const current = parseInt(input.value, 10) || 0;

      if (delta > 0) {
        const max = this.getMaxForItem(item);
        if (max !== null && current >= max) return;
      }

      input.value = Math.max(0, current + delta);
      this.updateStepperState(item);
      this.scheduleUpdate(input, 300);
    },

    handleRemove(btn) {
      const item = btn.closest('[data-cart-item]');
      if (!item) return;
      const input = item.querySelector('[data-quantity-input]');
      if (input) input.value = 0;
      // Clear any pending debounced update for this item — we'll fire immediately
      const pending = this.timers.get(item);
      if (pending) clearTimeout(pending);
      this.updateLine(item, 0);
    },

    scheduleUpdate(input, delay) {
      const item = input.closest('[data-cart-item]');
      if (!item) return;
      const pending = this.timers.get(item);
      if (pending) clearTimeout(pending);
      const t = setTimeout(() => {
        this.updateLine(item, parseInt(input.value, 10) || 0);
      }, delay);
      this.timers.set(item, t);
    },

    async updateLine(item, quantity) {
      item.classList.add('is-loading');
      const lineKey = item.dataset.lineKey;

      try {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ id: lineKey, quantity }),
        });

        if (!response.ok) throw new Error('Cart update failed: ' + response.status);
        const cart = await response.json();

        if (cart.item_count === 0) {
          window.location.reload();
          return;
        }

        if (quantity === 0) {
          item.classList.add('is-removing');
          setTimeout(() => item.remove(), 260);
        } else {
          const matched = cart.items.find((it) => it.key === lineKey);
          if (matched) {
            const linePriceEl = item.querySelector('[data-line-price]');
            if (linePriceEl) {
              linePriceEl.textContent = this.formatter.format(matched.final_line_price / 100);
            }
            // Server may have clamped the quantity (inventory_policy: deny) — reflect it
            const input = item.querySelector('[data-quantity-input]');
            if (input && parseInt(input.value, 10) !== matched.quantity) {
              input.value = matched.quantity;
            }
            this.updateStepperState(item);
          }
        }

        const subtotalEl = document.querySelector('[data-cart-subtotal]');
        if (subtotalEl) {
          subtotalEl.textContent = this.formatter.format(cart.total_price / 100);
        }

        const countEl = document.querySelector('[data-cart-item-count]');
        if (countEl) {
          countEl.textContent = `${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'}`;
        }

        document.querySelectorAll('[data-cart-count]').forEach((el) => {
          el.textContent = cart.item_count;
        });
      } catch (err) {
        console.error(err);
        // On failure, reload to recover accurate state
        window.location.reload();
      } finally {
        item.classList.remove('is-loading');
      }
    },
  };

  /* --------------------------------------------------------------------------
     Collection Sorting & Filtering
     -------------------------------------------------------------------------- */

  const CollectionSort = {
    init() {
      const sortSelect = document.querySelector('[data-sort-select]');
      if (!sortSelect) return;

      sortSelect.addEventListener('change', (e) => {
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', e.target.value);
        window.location.href = url.toString();
      });
    }
  };

  const CollectionFilters = {
    init() {
      const filterToggle = document.querySelector('[data-filters-toggle]');
      const filterContent = document.getElementById('filter-content');

      if (!filterToggle || !filterContent) return;

      filterToggle.addEventListener('click', () => {
        const isExpanded = filterToggle.getAttribute('aria-expanded') === 'true';
        filterToggle.setAttribute('aria-expanded', !isExpanded);
        filterContent.classList.toggle('is-open', !isExpanded);
      });
    }
  };

  /* --------------------------------------------------------------------------
     FAQ Accordion
     -------------------------------------------------------------------------- */

  const FAQAccordion = {
    init() {
      const accordions = document.querySelectorAll('[data-faq-accordion]');
      accordions.forEach(accordion => this.setupAccordion(accordion));
    },

    setupAccordion(accordion) {
      const toggles = accordion.querySelectorAll('[data-faq-toggle]');

      toggles.forEach(toggle => {
        toggle.addEventListener('click', () => {
          const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
          const answerId = toggle.getAttribute('aria-controls');
          const answer = document.getElementById(answerId);

          // Close other items (optional - remove this block for multi-open)
          toggles.forEach(otherToggle => {
            if (otherToggle !== toggle) {
              const otherAnswerId = otherToggle.getAttribute('aria-controls');
              const otherAnswer = document.getElementById(otherAnswerId);
              otherToggle.setAttribute('aria-expanded', 'false');
              if (otherAnswer) {
                otherAnswer.setAttribute('aria-hidden', 'true');
              }
            }
          });

          // Toggle current item
          toggle.setAttribute('aria-expanded', !isExpanded);
          if (answer) {
            answer.setAttribute('aria-hidden', isExpanded);
          }
        });
      });
    }
  };

  /* --------------------------------------------------------------------------
     Testimonials Slider
     -------------------------------------------------------------------------- */

  const TestimonialsSlider = {
    init() {
      this.sliders = document.querySelectorAll('[data-testimonials-slider]');
      this.sliders.forEach(slider => this.setupSlider(slider));
    },

    setupSlider(slider) {
      const track = slider.querySelector('.testimonials__track');
      const dots = slider.querySelectorAll('[data-dot]');
      const prevBtn = slider.querySelector('[data-prev]');
      const nextBtn = slider.querySelector('[data-next]');
      let currentIndex = 0;
      let itemsPerView = this.getItemsPerView();

      const updateSlider = () => {
        const cards = track.querySelectorAll('.testimonial-card');
        const maxIndex = Math.max(0, cards.length - itemsPerView);
        currentIndex = Math.min(currentIndex, maxIndex);

        const offset = (currentIndex / cards.length) * 100;
        track.style.transform = `translateX(-${offset}%)`;

        dots.forEach((dot, index) => {
          dot.classList.toggle('is-active', index === currentIndex);
        });
      };

      // Navigation
      if (prevBtn) {
        prevBtn.addEventListener('click', () => {
          if (currentIndex > 0) {
            currentIndex--;
            updateSlider();
          }
        });
      }

      if (nextBtn) {
        nextBtn.addEventListener('click', () => {
          const cards = track.querySelectorAll('.testimonial-card');
          const maxIndex = Math.max(0, cards.length - itemsPerView);
          if (currentIndex < maxIndex) {
            currentIndex++;
            updateSlider();
          }
        });
      }

      // Dots
      dots.forEach((dot, index) => {
        dot.addEventListener('click', () => {
          currentIndex = index;
          updateSlider();
        });
      });

      // Responsive
      window.addEventListener('resize', () => {
        itemsPerView = this.getItemsPerView();
        updateSlider();
      });
    },

    getItemsPerView() {
      if (window.innerWidth >= 990) return 3;
      if (window.innerWidth >= 750) return 2;
      return 1;
    }
  };

  /* --------------------------------------------------------------------------
     Login/Register Toggle
     -------------------------------------------------------------------------- */

  const LoginToggle = {
    init() {
      const recoverLink = document.querySelector('[data-recover-link]');
      const loginLink = document.querySelector('[data-login-link]');
      const loginForm = document.getElementById('CustomerLogin');
      const recoverForm = document.getElementById('RecoverPassword');

      if (recoverLink && loginForm && recoverForm) {
        recoverLink.addEventListener('click', (e) => {
          e.preventDefault();
          loginForm.style.display = 'none';
          recoverForm.style.display = 'block';
        });
      }

      if (loginLink && loginForm && recoverForm) {
        loginLink.addEventListener('click', (e) => {
          e.preventDefault();
          recoverForm.style.display = 'none';
          loginForm.style.display = 'block';
        });
      }
    }
  };

  /* --------------------------------------------------------------------------
     Address Form Toggle
     -------------------------------------------------------------------------- */

  const AddressToggle = {
    init() {
      const toggles = document.querySelectorAll('[data-add-address-toggle]');
      const form = document.getElementById('AddAddressForm');

      if (!toggles.length || !form) return;

      toggles.forEach(toggle => {
        toggle.addEventListener('click', () => {
          form.style.display = form.style.display === 'none' ? 'block' : 'none';
        });
      });
    }
  };

  /* --------------------------------------------------------------------------
     Product Recommendations
     -------------------------------------------------------------------------- */

  const ProductRecommendations = {
    init() {
      const sections = document.querySelectorAll('.product-recommendations');

      sections.forEach(section => {
        const url = section.dataset.url;
        if (!url) return;

        fetch(url)
          .then(response => response.text())
          .then(text => {
            const html = document.createElement('div');
            html.innerHTML = text;
            const recommendations = html.querySelector('.product-recommendations');

            if (recommendations && recommendations.innerHTML.trim().length) {
              section.innerHTML = recommendations.innerHTML;
            }
          })
          .catch(err => console.error('Error loading recommendations:', err));
      });
    }
  };

  /* --------------------------------------------------------------------------
     Initialize All
     -------------------------------------------------------------------------- */

  document.addEventListener('DOMContentLoaded', () => {
    Header.init();
    ProductForm.init();
    ProductGallery.init();
    Cart.init();
    CollectionSort.init();
    CollectionFilters.init();
    FAQAccordion.init();
    TestimonialsSlider.init();
    LoginToggle.init();
    AddressToggle.init();
    ProductRecommendations.init();
  });

})();
